import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { Comments } from '../../components';
import { THUMB_TYPE_DISLIKE, THUMB_TYPE_LIKE } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { useConfirmationModal } from '../../util/hooks';
import { CommentForm } from '../../forms';
import routeConfiguration from '../../routeConfiguration';

import css from './CommentsPage.module.css';

const MAX_VISIBLE_REVIEWS = 1;
const POINTS_MODAL_COOLDOWN_HOURS = 2; // Show modal once every 2 hours

// Helper function to check if we should show the points modal
const shouldShowPointsModal = () => {
  const lastShownKey = 'pointsModalLastShown';
  const lastShown = localStorage.getItem(lastShownKey);

  if (!lastShown) {
    return true;
  }

  const now = Date.now();
  const lastShownTime = parseInt(lastShown, 10);
  const hoursSinceLastShown = (now - lastShownTime) / (1000 * 60 * 60);

  return hoursSinceLastShown >= POINTS_MODAL_COOLDOWN_HOURS;
};

// Helper function to record that we showed the points modal
const recordPointsModalShown = () => {
  localStorage.setItem('pointsModalLastShown', Date.now().toString());
};

const redirectToLoginPage = (location, history) => {
  const state = {
    from: `${location.pathname}${location.search}${location.hash}`,
  };

  const routes = routeConfiguration();
  history.push(createResourceLocatorString('LoginPage', routes), state);
};

const CommentsHeading = ({ comments }) => (
  <div className={css.commentsHeadingContainer}>
    <h2 className={css.commentsHeading}>
      <FormattedMessage
        id="SectionComments.commentsHeading"
        values={{
          count: comments.length,
        }}
      />
    </h2>
  </div>
);

const CommentSection = props => {
  const {
    comments,
    currentUser,
    currentUserReactions,
    fetchCommentsError,
    handleCommentLikeOrDislike,
    handleEditComment,
    handleDeleteComment,
    handleCommentSubmit,
    handlePinComment,
    handleSendReply,
    sendCommentInProgress,
    sendCommentError,
    sendReplyInProgress,
    sendReplyError,
    editCommentInProgress,
    editCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    addCommentLikeInProgress,
    addCommentLikeError,
    addCommentDislikeInProgress,
    addCommentDislikeError,
    pinCommentInProgress,
    pinCommentError,
    canPinComment,
  } = props;

  const showCommentForm = currentUser && !fetchCommentsError;
  const commentsClassName = showCommentForm && comments.length > 0 ? css.commentsFeed : undefined;

  const sortedComments = [...comments].sort(
    (a, b) => (b.attributes?.isPinned ? 1 : 0) - (a.attributes?.isPinned ? 1 : 0)
  );
  return (
    <div className={css.sectionComments}>
      <CommentsHeading comments={comments} />
      {showCommentForm ? (
        <CommentForm
          id="CommentsPage.comments"
          currentUser={currentUser}
          sendCommentInProgress={sendCommentInProgress}
          sendCommentError={sendCommentError}
          onSubmit={handleCommentSubmit}
          onCancel={(values, form) => form.restart()}
        />
      ) : null}
      {fetchCommentsError ? (
        <p className={css.errorText}>
          <FormattedMessage id="LiveRoomPage.commentsError" />
        </p>
      ) : null}
      <Comments
        className={commentsClassName}
        comments={sortedComments}
        currentUser={currentUser}
        currentUserReactions={currentUserReactions}
        likeOrDislikeInProgress={addCommentLikeInProgress || addCommentDislikeInProgress}
        likeOrDislikeError={addCommentLikeError || addCommentDislikeError}
        editCommentInProgress={editCommentInProgress}
        editCommentError={editCommentError}
        deleteCommentInProgress={deleteCommentInProgress}
        deleteCommentError={deleteCommentError}
        currentDeleteCommentId={currentDeleteCommentId}
        deletedCommentIds={deletedCommentIds}
        sendReplyInProgress={sendReplyInProgress}
        sendReplyError={sendReplyError}
        pinCommentInProgress={pinCommentInProgress}
        pinCommentError={pinCommentError}
        handleCommentLikeOrDislike={handleCommentLikeOrDislike}
        handleEditComment={handleEditComment}
        handleDeleteComment={handleDeleteComment}
        handlePinComment={handlePinComment}
        handleSendReply={handleSendReply}
        canPinComment={canPinComment}
      />
    </div>
  );
};

const SectionComments = props => {
  const {
    intl,
    location,
    history,
    currentUser,
    currentUserReactions,
    comments,
    fetchCommentsError,
    sendCommentInProgress,
    sendCommentError,
    sendReplyInProgress,
    sendReplyError,
    editCommentInProgress,
    editCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    addCommentLikeInProgress,
    addCommentLikeError,
    addCommentDislikeInProgress,
    addCommentDislikeError,
    pinCommentInProgress,
    pinCommentError,
    onSendComment,
    onSendReply,
    onEditComment,
    onDeleteComment,
    onAddCommentLike,
    onAddCommentDislike,
    onPinComment,
    onOpenPointsModal,
    canPinComment,
  } = props;

  const {
    open: onOpenConfirmationModal,
    openWithHandlers: onOpenConfirmationModalWithHandlers,
    updateProps: onUpdateConfirmationModalProps,
    closeModal: onCloseConfirmationModal,
    modalElement,
  } = useConfirmationModal();

  const handleCommentLikeOrDislike = (type, reviewId) => {
    if (currentUser) {
      if (type === THUMB_TYPE_LIKE) {
        onAddCommentLike(reviewId);
      } else if (type === THUMB_TYPE_DISLIKE) {
        onAddCommentDislike(reviewId);
      } else {
        console.error(new Error('Unknown like or dislike type'), {
          likeOrDislikeType: type,
        });
      }
    } else {
      redirectToLoginPage(location, history);
    }
  };

  const handleDeleteComment = commentId => {
    onOpenConfirmationModalWithHandlers({
      id: 'LiveRoomPage.confirmationModal',
      title: intl.formatMessage({
        id: 'LiveRoomPage.deleteCommentConfirmationModalTitle',
      }),
      description: intl.formatMessage({
        id: 'LiveRoomPage.deleteCommentConfirmationModalDescription',
      }),
      onConfirm: async () => {
        try {
          // Set loading state on the confirm button
          onUpdateConfirmationModalProps({ inProgress: true });

          // Perform the deletion
          await onDeleteComment(commentId);

          // Close modal after successful deletion
          onCloseConfirmationModal();
        } catch (error) {
          // Handle error case - remove loading state but keep modal open
          onUpdateConfirmationModalProps({ inProgress: false });
          console.error('Failed to delete comment:', error);
        }
      },
      onCancel: () => {
        onCloseConfirmationModal();
      },
    });
  };

  const handleCommentSubmit = (values, form) => {
    const { comment } = values;
    onSendComment(comment).then(() => {
      form.restart();
      if (shouldShowPointsModal()) {
        onOpenPointsModal(5);
        recordPointsModalShown();
      }
    });
  };

  const handleSendReply = (parentId, comment) => {
    return onSendReply(parentId, comment).then(() => {
      if (shouldShowPointsModal()) {
        onOpenPointsModal(5);
        recordPointsModalShown();
      }
    });
  };

  return (
    <>
      <CommentSection
        comments={comments}
        maxVisibleReviews={MAX_VISIBLE_REVIEWS}
        currentUser={currentUser}
        currentUserReactions={currentUserReactions}
        handleCommentSubmit={handleCommentSubmit}
        handleEditComment={(commentId, comment) => onEditComment(commentId, comment)}
        handleDeleteComment={handleDeleteComment}
        handleCommentLikeOrDislike={handleCommentLikeOrDislike}
        handlePinComment={onPinComment}
        handleSendReply={handleSendReply}
        fetchCommentsError={fetchCommentsError}
        sendCommentInProgress={sendCommentInProgress}
        sendCommentError={sendCommentError}
        sendReplyInProgress={sendReplyInProgress}
        sendReplyError={sendReplyError}
        editCommentInProgress={editCommentInProgress}
        editCommentError={editCommentError}
        deleteCommentInProgress={deleteCommentInProgress}
        deleteCommentError={deleteCommentError}
        currentDeleteCommentId={currentDeleteCommentId}
        deletedCommentIds={deletedCommentIds}
        addCommentLikeInProgress={addCommentLikeInProgress}
        addCommentDislikeInProgress={addCommentDislikeInProgress}
        addCommentLikeError={addCommentLikeError}
        addCommentDislikeError={addCommentDislikeError}
        pinCommentInProgress={pinCommentInProgress}
        pinCommentError={pinCommentError}
        canPinComment={canPinComment}
      />
      {modalElement}
    </>
  );
};

export default SectionComments;
